import { useState } from 'react';
import { Lote } from '@libs/types';
import {
  LotesContainer,
  LoteCard,
  LoteHeaderButton,
  LoteTotal,
  Chevron,
  ItensTable,
  EmptyState,
} from './styles';

type EditalLotesListProps = {
  lotes: Lote[];
};

// Formatação monetária em pt-BR (R$). Fora do componente para não recriar a cada render.
const currencyBRL = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const formatBRL = (value?: number | null): string =>
  typeof value === 'number' ? currencyBRL.format(value) : '-';

const formatQuantidade = (value?: number | null): string =>
  typeof value === 'number' ? value.toLocaleString('pt-BR') : '-';

export const EditalLotesList = ({ lotes }: EditalLotesListProps) => {
  // Cada lote é um accordion; guardamos por índice quais estão abertos.
  const [lotesAbertos, setLotesAbertos] = useState<Record<number, boolean>>({});

  const alternarLote = (index: number) => {
    setLotesAbertos((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  if (!lotes || lotes.length === 0) {
    return <EmptyState>Não há lotes disponíveis.</EmptyState>;
  }

  // Ordena os lotes pelo número; itens sem número vão para o fim.
  const lotesOrdenados = [...lotes].sort((a, b) => {
    const numeroA = typeof a.numero === 'number' ? a.numero : Infinity;
    const numeroB = typeof b.numero === 'number' ? b.numero : Infinity;
    return numeroA - numeroB;
  });

  return (
    <LotesContainer>
      {lotesOrdenados.map((lote, loteIndex) => {
        const aberto = !!lotesAbertos[loteIndex];

        return (
          <LoteCard key={lote.uniqueId ?? loteIndex}>
            <LoteHeaderButton
              type="button"
              onClick={() => alternarLote(loteIndex)}
              aria-expanded={aberto}
            >
              <span className="title">Lote {lote.numero ?? loteIndex + 1}</span>
              <span className="right">
                <LoteTotal>Valor total: {formatBRL(lote.valorTotal)}</LoteTotal>
                <Chevron className={aberto ? 'open' : ''}>▾</Chevron>
              </span>
            </LoteHeaderButton>

            {aberto &&
              (lote.itens && lote.itens.length > 0 ? (
                <ItensTable>
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Produto</th>
                      <th>Qtd.</th>
                      <th>Unidade</th>
                      <th>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lote.itens.map((item, itemIndex) => (
                      <tr key={itemIndex}>
                        <td>{item.numero ?? itemIndex + 1}</td>
                        <td>{item.produto || '-'}</td>
                        <td>{formatQuantidade(item.quantidade)}</td>
                        <td>{item.unidadeMedida?.descricao || '-'}</td>
                        <td>{formatBRL(item.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </ItensTable>
              ) : (
                <EmptyState>Nenhum item neste lote.</EmptyState>
              ))}
          </LoteCard>
        );
      })}
    </LotesContainer>
  );
};
